import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  createWelcomeMessage,
  recommendGroup,
  runOnboardingTurn,
  runQuickAction,
} from './engine';
import {
  getReportSnapshot,
  subscribeReportSnapshot,
  summarizeIssue,
} from './productContext';
import {
  getStepFromPath,
  isOnboardingStep,
  isProductStep,
  STEP_META,
  STEPS,
} from './steps';

const OnboardingAgentContext = createContext(null);

const AGENT_EVENT = 'gitdone-agent';

export const emitAgentCommand = (type, detail = {}) => {
  window.dispatchEvent(
    new CustomEvent(AGENT_EVENT, {
      detail: { type, ...detail },
    }),
  );
};

export const useOnboardingAgent = () => {
  const ctx = useContext(OnboardingAgentContext);
  if (!ctx) {
    throw new Error(
      'useOnboardingAgent must be used within OnboardingAgentProvider',
    );
  }
  return ctx;
};

export const OnboardingAgentProvider = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const {
    groups,
    selectedGroups,
    user,
    issues,
    activity,
    members,
    projects,
  } = useSelector((state) => state.auth);

  const step = getStepFromPath(location.pathname);
  const shouldAutoOpen =
    isOnboardingStep(step) || isProductStep(step);

  const [open, setOpen] = useState(shouldAutoOpen);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [report, setReport] = useState(getReportSnapshot());
  const [focusedIssue, setFocusedIssue] = useState(null);
  const [reassignProposal, setReassignProposal] = useState(null);

  let userName = '';
  try {
    userName = user ? JSON.parse(user)?.name || '' : '';
  } catch (e) {
    userName = '';
  }

  const recommendedGroup = useMemo(() => recommendGroup(groups), [groups]);

  const ctx = useMemo(
    () => ({
      userName,
      groups,
      selectedGroups,
      recommendedGroup,
      issues,
      activity,
      members,
      projects,
      report,
      focusedIssue,
      reassignProposal,
    }),
    [
      userName,
      groups,
      selectedGroups,
      recommendedGroup,
      issues,
      activity,
      members,
      projects,
      report,
      focusedIssue,
      reassignProposal,
    ],
  );

  useEffect(() => {
    return subscribeReportSnapshot((snapshot) => {
      setReport(snapshot);
    });
  }, []);

  useEffect(() => {
    setOpen(shouldAutoOpen);
  }, [shouldAutoOpen, location.pathname]);

  useEffect(() => {
    setFocusedIssue(null);
    setMessages([createWelcomeMessage(step, {
      userName,
      groups,
      selectedGroups,
      recommendedGroup,
      issues,
      activity,
      members,
      projects,
      report: getReportSnapshot(),
    })]);
  }, [step]);

  const applyActions = useCallback(
    (actions = []) => {
      actions.forEach((action) => {
        switch (action.type) {
          case 'accept_terms':
            emitAgentCommand('accept_terms');
            break;
          case 'select_group':
            emitAgentCommand('select_group', { group: action.group });
            break;
          case 'continue_group':
            emitAgentCommand('continue_group');
            break;
          case 'select_all_members':
            emitAgentCommand('select_all_members');
            break;
          case 'select_report_group':
            emitAgentCommand('select_report_group', { group: action.group });
            break;
          case 'select_report_project':
            emitAgentCommand('select_report_project', {
              project: action.project,
            });
            break;
          case 'select_report_label':
            emitAgentCommand('select_report_label', { label: action.label });
            break;
          case 'store_reassign_proposal':
            setReassignProposal(action.proposal || null);
            break;
          case 'confirm_reassign':
            if (action.proposal) {
              emitAgentCommand('reassign_issue', {
                eventItem: action.proposal.event,
                assigneeId: action.proposal.assigneeId,
                assigneeName: action.proposal.assigneeName,
              });
              setReassignProposal(null);
            }
            break;
          case 'navigate':
            if (action.path) {
              history.push(action.path);
            }
            break;
          default:
            break;
        }
      });
    },
    [history],
  );

  const appendAgentText = useCallback(
    (text, nextStep = step) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `agent-${Date.now()}`,
          role: 'agent',
          text,
          step: nextStep,
        },
      ]);
    },
    [step],
  );

  useEffect(() => {
    const onAgentCommand = (event) => {
      const { type, eventItem, newEnd, assigneeName } = event.detail || {};
      if (type === 'ask_about_issue' && eventItem) {
        setOpen(true);
        setFocusedIssue(eventItem);
        setMessages((prev) => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            text: `Ask about issue: ${eventItem.title || eventItem.issue || ''}`,
            step: STEPS.REPORT,
          },
          {
            id: `agent-${Date.now()}-issue`,
            role: 'agent',
            text: summarizeIssue(eventItem),
            step: STEPS.REPORT,
          },
        ]);
      }
      if (type === 'schedule_updated' && eventItem) {
        setOpen(true);
        setMessages((prev) => [
          ...prev,
          {
            id: `agent-${Date.now()}-schedule`,
            role: 'agent',
            text: [
              `Saved GitLab due date for “${eventItem.title}”`,
              newEnd ? `→ ${String(newEnd).slice(0, 10)}.` : '.',
              'Refresh filters if the bar still looks stale.',
            ].join(' '),
            step: STEPS.REPORT,
          },
        ]);
      }
      if (type === 'reassign_issue_result') {
        let text = `Reassign failed: ${event.detail?.message || 'unknown error'}`;
        if (event.detail?.ok) {
          text = `Assignee updated to ${assigneeName || 'selected member'} in GitLab.`;
        }
        setMessages((prev) => [
          ...prev,
          {
            id: `agent-${Date.now()}-reassign`,
            role: 'agent',
            text,
            step: STEPS.REPORT,
          },
        ]);
      }
    };
    window.addEventListener(AGENT_EVENT, onAgentCommand);
    return () => window.removeEventListener(AGENT_EVENT, onAgentCommand);
  }, []);

  const sendText = useCallback(
    (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed) {
        return;
      }

      const userMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        text: trimmed,
        step,
      };

      const { replies, actions } = runOnboardingTurn({
        step,
        text: trimmed,
        ctx,
      });

      setMessages((prev) => [...prev, userMessage, ...replies]);
      applyActions(actions);
      setDraft('');
    },
    [applyActions, ctx, step],
  );

  const triggerQuickAction = useCallback(
    (actionId) => {
      const label = actionId
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      const userMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        text: label,
        step,
      };
      const { replies, actions } = runQuickAction(actionId, step, ctx);
      setMessages((prev) => [...prev, userMessage, ...replies]);
      applyActions(actions);
    },
    [applyActions, ctx, step],
  );

  const value = {
    open,
    setOpen,
    messages,
    draft,
    setDraft,
    sendText,
    triggerQuickAction,
    appendAgentText,
    step,
    stepMeta: STEP_META[step] || STEP_META[STEPS.OTHER],
    recommendedGroup,
  };

  return (
    <OnboardingAgentContext.Provider value={value}>
      {children}
    </OnboardingAgentContext.Provider>
  );
};

export default OnboardingAgentProvider;
